// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import * as moment from "moment";
import "moment/locale/fr";
import { Account, IAccount } from "../helpers/account-helper";
import { FHIRHelper } from "../helpers/FHIR-helper";
import { v4 as uuidv4 } from "uuid";
import { IEntity, Identifier, Codes, Reference, ACTION_STATUS_ENTITY, STATUS_ENTITY } from "./sharedInterfaces";
import { FileLogger } from "../helpers/fileLogger";

/**
 *  Interfaces for  Appointment
 */
export interface IAppointment extends IEntity {
  resourceType: string;
  identifier: Identifier[];
  status: string; // "booked" for user appointement, set something else for hospital appointement
  description: string;
  participant: Participant[];
  comment?: string;
  patientComment?: string; // NOT FHIR format: user specific comment for this appointment
  start: string; // moment start date
  end: string; // moment end date
  serviceType?: Codes;
  appointmentType?: Codes;
  reason?: Codes;
  supportingInformation?: Reference[];
  dateSeenByPatient?: string; // The date at which the patient saw the data of this appointment
  dateSeenByPatientHistory?: string[]; // The list of dates on which the patient has seen this appointment with old data
}

export interface Participant {
  actor?: Reference;
  required?: string;
  status?: string; // accepted, accompagnied (for related)
  participantType?: Codes; // participant type: person or location
}

/**
 * Helper for appointement
 */
export class Appointment {
  public static get PARTICIPANT_STATUS_ACCEPTED(): string {
    return "accepted";
  }
  public static get PARTICIPANT_STATUS_ACCOMPAGNIED(): string {
    return "accompagnied";
  }

  public static get STATUS_BOOKED(): string {
    return "booked";
  }

  public static get TYPE_CAREPLANLINK(): string {
    return "careplanlink";
  } // appointment linked to/from a careplan
  public static get TYPE_PERSONAL(): string {
    return "personal";
  } // appointment created by user
  public static get TYPE_HOSPITAL(): string {
    return "hospital";
  } // appointment created by hospital

  public static get PARTICIPANT_TYPE_LOCATION(): string {
    return "location";
  }
  public static get PARTICIPANT_TYPE_ADDRESS(): string {
    return "address";
  }
  public static get PARTICIPANT_TYPE_PERSON(): string {
    return "person";
  }
  /**
   * generate a basic appointment
   * @param momentDate
   * @param account
   */
  public static createAppointmentObject(momentDate: string, account: IAccount): IAppointment {
    // create new appointment
    const app: IAppointment = {
      _id: undefined,
      modified: moment().format(),
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      entityStatus: [STATUS_ENTITY.ACTIVE],
      resourceType: FHIRHelper.RES_APPOINTMENT,
      identifier: [
        {
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: uuidv4(),
        },
      ],
      status: Appointment.STATUS_BOOKED,
      description: "",
      participant: [
        {
          actor: Account.account2Reference(account),
          required: "required",
          status: Appointment.PARTICIPANT_STATUS_ACCEPTED,
          participantType: {
            text: Appointment.PARTICIPANT_TYPE_PERSON,
          },
        },
      ],
      comment: "",
      patientComment: "",
      start: momentDate,
      end: moment(momentDate).add(1, "hour").format(),
      appointmentType: {
        text: Appointment.TYPE_PERSONAL, // for user appointement
      },
    };
    return app;
  }

  /**
   * Check if user is a participant of the appointment
   * @param app
   * @param user
   */
  public static isParticipant(app: IAppointment, user: string): boolean {
    try {
      for (const participant of app.participant) {
        if (participant.actor.reference === user) return true;
      }
    } catch (err) {
      FileLogger.error("Appointment", "isParticipant", err);
    }
    return false;
  }

  /**
   * check if user come with related or not
   * @param app
   */
  public static isAccompagnied(app: IAppointment, user: string): boolean {
    const p = app.participant?.find((participant) => {
      return participant.actor.reference === user && participant.status === Appointment.PARTICIPANT_STATUS_ACCOMPAGNIED;
    });
    return p !== undefined;
  }

  /**
   * Is patient accompanied by a related ?
   * @param app
   */
  public static amIaccompanied(app: IAppointment, user: string): boolean {
    let IamOwner = false;
    let IamAccompanied = false;
    for (const participant of app.participant) {
      if (participant.actor.reference === user && participant.status === Appointment.PARTICIPANT_STATUS_ACCEPTED) {
        IamOwner = true;
      }
      if (participant.status === Appointment.PARTICIPANT_STATUS_ACCOMPAGNIED) {
        IamAccompanied = true;
      }
    }
    return IamOwner && IamAccompanied;
  }

  /**
   * Gather related who are accompnying patient
   * @param app
   */
  public static getRelatedAccompanying(app: IAppointment): string[] {
    const names = [];
    for (const participant of app.participant) {
      if (participant.status === Appointment.PARTICIPANT_STATUS_ACCOMPAGNIED) {
        names.push(participant.actor.display);
      }
    }
    return names;
  }

  /**
   * Is appointement created by user ?
   * @param app
   */
  public static isPersonalAppointment(app: IAppointment): boolean {
    if (app.appointmentType && app.appointmentType.text === Appointment.TYPE_PERSONAL) return true;
    return false;
  }

  /**
   * return user custom address for this appointment
   * @param app
   */
  public static getCustomLocation(app: IAppointment): Reference {
    for (const participant of app.participant) {
      if (participant.participantType && participant.participantType.text === Appointment.PARTICIPANT_TYPE_ADDRESS) {
        return participant.actor;
      }
    }
    return null;
  }

  /**
   * return user custom address for this appointment
   * @param app
   */
  public static setCustomLocation(app: IAppointment, location: string): Reference {
    const currentLocation = Appointment.getCustomLocation(app);
    if (currentLocation === null) {
      // doesn't exists yet
      if (!app.participant || app.participant.length === 0) app.participant = [];
      app.participant.push({
        actor: {
          reference: FHIRHelper.SYSTEM_CAREMATE,
          display: location.trim() + "",
        },
        participantType: {
          text: Appointment.PARTICIPANT_TYPE_ADDRESS,
        },
      });
    } else {
      // update current
      currentLocation.display = location;
    }
    return currentLocation;
  }
}
